import { ConfigProvider, message } from "antd";
import { Outlet, useLocation } from "react-router-dom";
import styled from "styled-components";
import { NavBar } from "../components/general";
import { NavFooter } from "../components";
import { AppLayout, ContentLayout } from "./Layout";
import OffscreenTrigger from "../components/OffscreenTrigger";
import AuthProvider from "../context/AuthProvider";
import ScrollOffscreenProvider from "../context/ScrollOffscreenProvider";
import BackLocationsProvider from "../context/BackLocationsProvider";
import { WidgetProvider } from "../context/WidgetContext";
import { MobileWidgetProvider } from "../context/MobileWidgetContext";
import { useEffect } from "react";
import ContentGate from "../components/ContentGate";
import SignupModal from "../components/login/SignupModal";

import { APP_NAME } from "../helpers/constants";
import { showHeaderUi, showFooterUi } from "../helpers/general";
import {
  trackActivityEvent,
  installGlobalActivityListeners,
} from "../lib/analytics/trackActivityEvent";
import { Helmet } from "react-helmet";
import { Spacer } from "../styles/CommonElements";
import { device } from "../lib/breakpoints";
import AIAssistantWrapper from "../components/AIAssistantWrapper";
import FeedbackWidgetWrapper from "../components/FeedbackWidgetWrapper";
import OnboardingWidgetWrapper from "../components/OnboardingWidgetWrapper";
import PostLoginRedirect from "../components/PostLoginRedirect";
import FirstTimeUserModalWrapper from "../components/FirstTimeUserModalWrapper";
import OpsChatFirstTimeModalWrapper from "../components/OpsChatFirstTimeModalWrapper";

// Light theme following LandlordOps branding guide
export const antdLightTheme = {
  token: {
    fontFamily: "Inter",
    colorPrimary: "var(--color-primary)",
    colorBgContainer: "var(--color-bg-secondary)",
    colorBgElevated: "var(--color-bg-secondary)",
    colorBgLayout: "var(--color-bg-primary)",
    colorText: "var(--color-text)",
    colorTextSecondary: "var(--color-text-light)",
    colorTextTertiary: "var(--color-text-muted)",
    colorBorder: "var(--color-border-light)",
    colorBorderSecondary: "var(--color-border-medium)",
    borderRadius: 12,
    boxShadow: "0 4px 20px var(--shadow-light)",
    boxShadowSecondary: "0 2px 8px var(--shadow-light)",
  },
  components: {
    Layout: {
      bodyBg: "var(--color-bg-primary)",
      headerBg: "var(--color-bg-secondary)",
      siderBg: "var(--color-bg-secondary)",
    },
    Card: {
      colorBgContainer: "var(--color-bg-secondary)",
      colorBorderSecondary: "var(--color-border-light)",
      borderRadius: 16,
      boxShadow: "0 4px 20px var(--shadow-light)",
    },
    Table: {
      colorBgContainer: "var(--color-bg-secondary)",
      headerBg: "var(--color-bg-tertiary)",
      headerColor: "var(--color-text)",
      rowHoverBg: "var(--color-bg-tertiary)",
      borderColor: "var(--color-border-light)",
    },
    Button: {
      borderRadius: 12,
      primaryShadow: "0 4px 18px var(--shadow-medium)",
      defaultShadow: "0 2px 8px var(--shadow-light)",
    },
    Input: {
      colorBgContainer: "var(--color-bg-secondary)",
      colorBorder: "var(--color-border-light)",
      borderRadius: 8,
    },
    Select: {
      colorBgContainer: "var(--color-bg-secondary)",
      colorBorder: "var(--color-border-light)",
      borderRadius: 8,
      // Ensure arrow is vertically centered
      controlHeight: 48,
    },
    Modal: {
      colorBgElevated: "var(--color-bg-secondary)",
      borderRadius: 16,
      boxShadow: "0 8px 32px var(--shadow-medium)",
    },
    Popover: {
      colorBgElevated: "var(--color-bg-secondary)",
      borderRadius: 12,
      boxShadow: "0 4px 20px var(--shadow-medium)",
    },
    Notification: {
      colorBgElevated: "var(--color-bg-secondary)",
      colorText: "var(--color-text)",
      borderRadius: 12,
      boxShadow: "0 4px 20px var(--shadow-medium)",
    },
    Message: {
      colorBgElevated: "white",
      borderRadius: 8,
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
      colorText: "var(--color-text)",
    },
    Skeleton: {
      colorFill: "var(--color-bg-tertiary)",
      colorFillContent: "var(--color-border-light)",
    },
    Menu: {
      colorBgContainer: "var(--color-bg-secondary)",
      colorItemBg: "transparent",
      colorItemBgSelected: "var(--color-primary-glow)",
      colorItemText: "var(--color-text)",
      colorItemTextSelected: "var(--color-primary)",
      borderRadius: 8,
    },
    Dropdown: {
      colorBgElevated: "var(--color-bg-secondary)",
      borderRadius: 12,
      boxShadow: "0 4px 20px var(--shadow-medium)",
    },
    Tooltip: {
      colorBgSpotlight: "var(--color-text)",
      borderRadius: 8,
      // Disable tooltips on mobile
      ...(typeof window !== "undefined" &&
        window.innerWidth <= 768 &&
        {
          // On mobile, tooltips won't show
        }),
    },
    DatePicker: {
      colorBgContainer: "var(--color-bg-secondary)",
      colorBorder: "var(--color-border)",
      colorText: "var(--color-text)",
      colorTextPlaceholder: "var(--color-text-light)",
      borderRadius: 8,
      boxShadow: "0 4px 20px var(--shadow-medium)",
    },
    Calendar: {
      colorBgContainer: "var(--color-bg-secondary)",
      colorBorder: "var(--color-border)",
      colorText: "var(--color-text)",
      borderRadius: 8,
      boxShadow: "0 4px 20px var(--shadow-medium)",
    },
  },
};

// Configure global message settings
message.config({
  top: 16,
  duration: 2.5, // Reduced from default 3 seconds
  maxCount: 3,
  rtl: false,
});

export default function MainLayout() {
  const location = useLocation();
  const isHomePage = location.pathname === "/";
  const isHeaderVisible = showHeaderUi(location);
  const isFooterVisible = showFooterUi(location);

  useEffect(() => {
    installGlobalActivityListeners();
  }, []);

  useEffect(() => {
    window.scroll(0, 0);
    trackActivityEvent("page_view");
  }, [location.pathname, location.search]);

  return (
    <ConfigProvider theme={antdLightTheme}>
      <Helmet>
        <title>{APP_NAME}</title>
        <link rel="icon" href={`/favicon-${APP_NAME.toLowerCase()}.ico`} />
      </Helmet>
      <AuthProvider>
        <PostLoginRedirect />
        <BackLocationsProvider>
          <ScrollOffscreenProvider>
            <WidgetProvider>
              <MobileWidgetProvider>
                <AppLayout>
                  <Container $headerVisible={isHeaderVisible}>
                    {isHeaderVisible && <NavBar />}
                    <ContentLayout>
                      <Outlet />
                    </ContentLayout>
                    {isFooterVisible && <NavFooter />}
                  </Container>
                  <OffscreenTrigger />
                </AppLayout>
                <ContentGate />
                <SignupModal />
                {/* First Time User Modal - Shows on first login (LandlordOps full suite) */}
                <FirstTimeUserModalWrapper />
                {/* OpsChat First Time Modal - Shows on first login for OpsChat-only users */}
                <OpsChatFirstTimeModalWrapper />
                {/* Onboarding Widget - Shows when tasks are incomplete */}
                <OnboardingWidgetWrapper />
                {/* AI Assistant Widget - Global Floating Chat Interface */}
                <AIAssistantWrapper />
                {/* Feedback Widget - Global Feedback Collection */}
                <FeedbackWidgetWrapper />
              </MobileWidgetProvider>
            </WidgetProvider>
          </ScrollOffscreenProvider>
        </BackLocationsProvider>
      </AuthProvider>
    </ConfigProvider>
  );
}

const Container = styled.div`
  margin-top: ${(props) =>
    props.$headerVisible ? "var(--navbar-height)" : "0"};

  @media ${device.mobile} {
    margin-top: ${(props) =>
      props.$headerVisible ? "var(--navbar-height-mobile)" : "0"};
  }

  .ant-menu-item {
    margin-bottom: 14px !important;
  }
`;
